'use client'

import { useState, type FormEvent, type ReactNode } from 'react'
import { StreamEvent } from '@langchain/core/tracers/log_stream'
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom'
import { ArrowDown } from 'lucide-react'

import Button from '@/components/ui/Button'
import Spinner from '@/components/Spinner'
import Animate from '@/components/Animate'
import { cn } from '@/lib/utils/client'
export interface StreamedChatMessage {
  type: string
  content?: string
  streamEvents?: StreamEvent[]
}

export const ChatInput = (props: {
  onSubmit: (e: FormEvent<HTMLFormElement>) => void
  onStop?: () => void
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  loading?: boolean
  placeholder?: string
  children?: ReactNode
  className?: string
  actions?: ReactNode
}) => (
  <form
    onSubmit={e => {
      e.stopPropagation()
      e.preventDefault()

      if (props.loading) {
        props.onStop?.()
      } else {
        props.onSubmit(e)
      }
    }}
    className={cn('flex w-full flex-col', props.className)}
  >
    <div className='border-1 border-gray-300 bg-secondary rounded-lg flex flex-col gap-2 max-w-[768px] w-full my-6 mx-auto'>
      <input
        value={props.value}
        placeholder={props.placeholder}
        onChange={props.onChange}
        className='border-none outline-none bg-transparent p-4'
      />

      <div className='flex justify-between ml-4 mr-2 mb-2'>
        <div className='flex gap-3'>{props.children}</div>

        <div className='flex gap-2 self-end'>
          {props.actions}
          <Button
            type='submit'
            disabled={props.loading && props.onStop == null}
          >
            {props.loading ? (
              <Spinner className='h-6 w-6' />
            ) : (
              <span>Send</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  </form>
)

export function StatefulChatInput(props: {
  loading?: boolean
  onSubmit: (value: string) => void
  onStop?: () => void
}) {
  const [input, setInput] = useState('')

  return (
    <ChatInput
      loading={props.loading}
      value={input}
      onChange={e => setInput(e.target.value)}
      onStop={props.onStop}
      onSubmit={e => {
        e.preventDefault()
        setInput('')
        props.onSubmit(input)
      }}
    />
  )
}

const ScrollToBottom: React.FC = () => {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext()

  return (
    <Animate
      visible={!isAtBottom}
      className='absolute left-[50%] bottom-0 translate-x-[-50%] mb-4'
      translateY={10}
    >
      <Button sm onClick={() => scrollToBottom()}>
        <ArrowDown className='h-4 w-4' />
        Scroll to bottom
      </Button>
    </Animate>
  )
}

const ChatContent = (props: {
  children: ReactNode
  className?: string
  contentClassName?: string
  showScrollButton?: boolean
}) => (
  <div className='relative w-full flex flex-col overflow-hidden'>
    <StickToBottom.Content className='flex flex-col gap-4'>
      <div className={cn('flex-1 overflow-y-auto', props.contentClassName)}>
        {props.children}
      </div>
      {props.showScrollButton && <ScrollToBottom />}
    </StickToBottom.Content>
  </div>
)

export const ChatBox = (props: {
  children: ReactNode
  showScrollButton?: boolean
}) => (
  <div className='flex flex-col gap-2 w-full overflow-hidden'>
    <StickToBottom
      className='h-[30vh] flex flex-col'
      resize='smooth'
      initial={{ mass: 10 }}
    >
      {props.children && (
        <ChatContent
          showScrollButton={props.showScrollButton}
          contentClassName='py-8 px-2'
        >
          {props.children}
        </ChatContent>
      )}
    </StickToBottom>
  </div>
)

function EditMessage({
  message,
  onEdit,
  onCancel
}: {
  message?: StreamedChatMessage
  onEdit: (message: StreamedChatMessage) => void
  onCancel: () => void
}) {
  const [editValue, setEditValue] = useState(message?.content as string)

  return (
    <ChatInput
      value={editValue}
      onChange={e => setEditValue(e.target.value)}
      onSubmit={e => {
        e.preventDefault()
        onEdit({ type: 'human', content: editValue })
      }}
      actions={
        <button type='button' onClick={onCancel}>
          Cancel
        </button>
      }
    />
  )
}

export function ChatMessage(props: {
  message?: StreamedChatMessage
  streamEvents?: StreamEvent[]
  onEdit: (message: StreamedChatMessage) => void
  onRegenerate: () => void
  actions?: ReactNode
}) {
  const [isEditing, setIsEditing] = useState(false)

  if (isEditing) {
    return (
      <EditMessage
        message={props.message}
        onEdit={props.onEdit}
        onCancel={() => setIsEditing(false)}
      />
    )
  }

  return (
    <div
      className={cn(
        'text-left',
        props.message?.type === 'human' ? 'ml-auto' : 'mr-auto'
      )}
    >
      <div
        className={cn(
          `rounded-[24px] flex`,
          props.message?.type === 'human' ? 'bg-secondary px-4 py-2' : null
        )}
      >
        <div className='whitespace-pre-wrap flex flex-col'>
          {
            // !props.message?.content &&
            props?.streamEvents
              ? props?.streamEvents
                  ?.map(m => m.data.chunk.kwargs.content)
                  .join('')
              : props.message?.content
          }
        </div>
      </div>

      {props.message?.type === 'human' && (
        <div className='ml-auto flex justify-end items-center gap-2 mt-2'>
          <button
            className='text-right text-sm'
            type='button'
            onClick={() => setIsEditing(true)}
          >
            Edit
          </button>

          {props.actions}
        </div>
      )}

      {props.message?.type === 'ai' && (
        <div className='mt-2 flex items-center justify-start gap-2'>
          {props.actions}
          <div className='text-sm'>
            <button type='button' onClick={props.onRegenerate}>
              Regenerate
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
